import classNames from 'classnames';
import { Button, FileInput, Label, TextInput } from 'flowbite-react';
import { get } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { Dropdown, gradYearOptions, postgraduateDegrees, undergraduateDegrees } from 'shared-utils';
import CustomDropDown from 'src/components/shared/CustomDropDown';
import CollegeSelect from 'src/components/shared/CollegeSelect';
import { HiOutlineArrowRight } from 'react-icons/hi';
import { useToast } from 'src/context/ToastProvider';
import {
	PostgraduateDegreeTypes,
	ProfileFormData,
	ScoreType,
	UndergraduateDegreeTypes,
	UserExperienceLevelType,
} from 'types';
import { getPolicyForS3NonVideo, updateUserProfileData } from 'src/apis/user';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';

const MandatoryForm = () => {
	const toast = useToast();
	const { user } = useUserContext();
	const [searchParams] = useSearchParams();
	const roundTemplateId = searchParams.get('roundTemplateId');
	const interviewType = searchParams.get('interviewType');
	const [resume, setResume] = useState<File | null>(null);
	const [collegeOptions, setCollegeOptions] = useState<Dropdown | null>(null);
	const [specialisationOptions, setSpecialisationOptions] = useState<Dropdown[]>([]);
	const [formData, setFormData] = useState<ProfileFormData>({
		resume: user?.profile?.resumeLink ?? user?.pifMeta?.resumeLink ?? '',
		tenthScoreType: user?.pifMeta?.tenth?.scoreType ?? ScoreType.PERCENTAGE,
		twelfthScoreType: user?.pifMeta?.twelfth?.scoreType ?? ScoreType.PERCENTAGE,
		currentScoreType: user?.pifMeta?.underGraduate?.scoreType ?? ScoreType.PERCENTAGE,
		tenthScore: user?.pifMeta?.tenth?.percentage ?? '',
		twelfthScore: user?.pifMeta?.twelfth?.percentage ?? '',
		currentScore: user?.pifMeta?.underGraduate?.percentage ?? user?.pifMeta?.postGraduate?.percentage ?? '',
		gradYear: user?.profile?.gradYear?.toString() ?? '',
		college: user?.pifMeta?.underGraduate?.college?._id ?? user?.pifMeta?.postGraduate?.college?._id ?? '',
		degree: user?.pifMeta?.underGraduate?.degree ?? user?.pifMeta?.postGraduate?.degree ?? '',
		specialisation: user?.pifMeta?.underGraduate?.specialisation ?? user?.pifMeta?.postGraduate?.specialisation ?? '',
		gender: (user?.pifMeta?.gender || user?.profile?.gender) ?? 'Male',
	});

	const updateFormData = (field: keyof ProfileFormData, value: string | ScoreType | string[] | boolean) => {
		setFormData(prevData => ({ ...prevData, [field]: value }));
	};

	const options = [
		{ label: 'Percentage (100)', value: 'percentage', outOf: 100 },
		{ label: 'GPA (4)', value: 'gpa', outOf: 4 },
		{ label: 'CGPA (10)', value: 'cgpa', outOf: 10 },
	];

	const futureGradYearOptions = gradYearOptions.filter(op => Number(op.value) >= moment().subtract(5, 'year').year());
	const tenthScoreOptions = options?.find(option => option.value === formData.tenthScoreType);
	const twelfthScoreOptions = options?.find(option => option.value === formData.twelfthScoreType);
	const currentScoreOptions = options?.find(option => option.value === formData.currentScoreType);
	const collegeName = user?.pifMeta?.underGraduate?.college?.name ?? user?.pifMeta?.postGraduate?.college?.name ?? '';

	const degreeOptions = [
		...Object.keys(UndergraduateDegreeTypes).map(el => ({
			label: el,
			value: UndergraduateDegreeTypes[el as keyof typeof UndergraduateDegreeTypes],
		})),
		...Object.keys(PostgraduateDegreeTypes).map(el => ({
			label: el,
			value: PostgraduateDegreeTypes[el as keyof typeof PostgraduateDegreeTypes],
		})),
	];

	useEffect(() => {
		if (undergraduateDegrees[formData.degree as keyof typeof undergraduateDegrees]) {
			setSpecialisationOptions(
				undergraduateDegrees[formData.degree as keyof typeof undergraduateDegrees]?.map(el => ({
					label: el,
					value: el,
				})),
			);
		} else if (postgraduateDegrees[formData.degree as keyof typeof postgraduateDegrees]) {
			setSpecialisationOptions(
				postgraduateDegrees[formData.degree as keyof typeof postgraduateDegrees]?.map(el => ({ label: el, value: el })),
			);
		} else {
			setSpecialisationOptions([]);
		}
	}, [formData.degree]);

	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const updateUserProfileDataFn = useMutation({
		mutationFn: updateUserProfileData,
		onSuccess: () => {
			toast({
				title: 'Profile updated',
			});
			queryClient.refetchQueries(['verifyCookie']);
			navigate(`/benchmarking/take-photo?roundTemplateId=${roundTemplateId}&meetingType=${interviewType}`);
		},
		onError: (error: Error) => {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		let resumeUrl = '';
		if (resume) {
			const fileSizeMB = resume.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'Resume File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			const policy = await getPolicyForS3NonVideo(
				resume.name?.trim()?.replaceAll(' ', '_'),
				'resumeUrls',
				resume.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, resume);
			if (uploadedUrl) resumeUrl = uploadedUrl;
		}

		updateUserProfileDataFn.mutate({ ...formData, resume: resumeUrl });
	};

	return (
		<div className="max-w-[1480px] w-full mx-auto px-4 lg:px-[80px] [&_input]:bg-white pb-2">
			<form className="max-w-[904px]" onSubmit={handleSubmit}>
				<div className="flex items-end gap-3 pt-[20px] flex-wrap md:flex-nowrap">
					<h2 className="text-xl font-semibold w-fit whitespace-pre">Education details</h2>
					<span className="text-xs text-[#6B7280] font-[400] italic md:whitespace-pre">
						Please note that these are mandatory details required to generate your profile and these cannot be edited
						later apart from ‘Resume’{' '}
					</span>
					<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="10th-score" value="10th Score" />
							<span className="text-[red]"> *</span>
						</div>
						<TextInput
							color="purple"
							id="10th-score"
							type="number"
							className="RemoveInputArrow [&_input]:pr-[193px]"
							sizing="lg"
							required
							min={0}
							step="0.01"
							disabled={!!user?.pifMeta?.tenth?.percentage}
							max={tenthScoreOptions?.outOf}
							placeholder={`Out of ${tenthScoreOptions?.outOf}`}
							value={formData.tenthScore}
							onChange={e => {
								if (user?.pifMeta?.tenth?.percentage) {
									return;
								}
								if (
									(tenthScoreOptions && Number(e.target.value) > tenthScoreOptions?.outOf) ||
									Number(e.target.value) < 0
								) {
									return;
								}
								updateFormData('tenthScore', e.target.value);
							}}
							rightIcon={() => {
								return (
									<div className="pointer-events-auto border-l-[2px] -mr-3 border-[#D1D5DB] h-full flex items-center px-4 relative min-w-[194px] justify-between">
										<CustomDropDown
											disabled={!!user?.pifMeta?.tenth?.scoreType}
											buttonClassName="justify-between"
											label={tenthScoreOptions?.label || ''}
											buttonStyle={{
												color: '#6B7280',
											}}
											options={options}
											onChange={value => {
												if (user?.pifMeta?.tenth?.scoreType) {
													return;
												}
												updateFormData('tenthScoreType', value as ScoreType);
												updateFormData('tenthScore', '');
											}}
										/>
									</div>
								);
							}}
						/>
					</div>
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="12th-score" value="12th Score" />
							<span className="text-[red]"> *</span>
						</div>
						<TextInput
							color="purple"
							id="12th-score"
							type="number"
							className="RemoveInputArrow [&_input]:pr-[193px]"
							sizing="lg"
							required
							min={0}
							step="0.01"
							value={formData.twelfthScore}
							disabled={!!user?.pifMeta?.twelfth?.percentage}
							max={twelfthScoreOptions?.outOf}
							placeholder={`Out of ${twelfthScoreOptions?.outOf}`}
							onChange={e => {
								if (user?.pifMeta?.twelfth?.percentage) {
									return;
								}
								if (
									(twelfthScoreOptions && Number(e.target.value) > twelfthScoreOptions?.outOf) ||
									Number(e.target.value) < 0
								) {
									return;
								}
								updateFormData('twelfthScore', e.target.value);
							}}
							rightIcon={() => {
								return (
									<div className="pointer-events-auto border-l-[2px] -mr-3 border-[#D1D5DB] h-full flex items-center px-4 relative min-w-[194px] justify-between">
										<CustomDropDown
											disabled={!!user?.pifMeta?.twelfth?.scoreType}
											buttonClassName="justify-between"
											label={twelfthScoreOptions?.label || ''}
											buttonStyle={{
												color: '#6B7280',
											}}
											options={options}
											onChange={value => {
												if (user?.pifMeta?.twelfth?.scoreType) {
													return;
												}
												updateFormData('twelfthScoreType', value as ScoreType);
												updateFormData('twelfthScore', '');
											}}
										/>
									</div>
								);
							}}
						/>
					</div>
				</div>

				<hr className="my-7" />

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="collegeName" value="University Name/ College Name" />
							<span className="text-[red]"> *</span>
						</div>

						<CollegeSelect
							disabled={
								!!user?.pifMeta?.underGraduate?.collegeUniversityName ||
								!!user?.pifMeta?.postGraduate?.collegeUniversityName
							}
							value={
								formData?.college && collegeName
									? { label: collegeName, value: collegeName }
									: collegeOptions
									  ? collegeOptions
									  : undefined
							}
							onChange={value => {
								if (
									user?.pifMeta?.underGraduate?.collegeUniversityName ||
									user?.pifMeta?.postGraduate?.collegeUniversityName
								) {
									return;
								}
								if (value) {
									if (Array.isArray(value)) {
										return;
									} else {
										setCollegeOptions(value);
										updateFormData('college', value.value);
									}
								} else {
									setCollegeOptions(null);
									updateFormData('college', '');
								}
							}}
						/>
					</div>

					<div className="md:flex-1 w-full">
						{user?.userPreference?.experienceLevel !== UserExperienceLevelType.FINAL_YEAR_STUDENT ? (
							<>
								<div className="mb-2">
									<Label htmlFor="pincode" value="Pincode" />
									<span className="text-[red]"> *</span>
								</div>
								<TextInput
									color="purple"
									id="pincode"
									type="number"
									className="RemoveInputArrow [&_input]:pr-[193px]"
									sizing="lg"
									required
									min={0}
									step="0.01"
									disabled={!!user?.pifMeta?.location?.pincode}
									value={formData.pincode}
									placeholder={`Pincode`}
									onChange={e => {
										if (user?.pifMeta?.location?.pincode) {
											return;
										}
										if (Number(e.target.value) < 0) {
											return;
										}
										updateFormData('pincode', e.target.value);
									}}
								/>
							</>
						) : null}
					</div>
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="degree" value="UG/PG Degree" />
							<span className="text-[#555770] text-xs"> (Whichever is latest)</span>
							<span className="text-[red]"> *</span>
						</div>

						<Select
							required
							id="degree"
							isClearable
							classNamePrefix="flowbite-select"
							isDisabled={!!user?.pifMeta?.underGraduate?.degree || !!user?.pifMeta?.postGraduate?.degree}
							value={degreeOptions?.find(item => item.value === formData.degree)}
							onChange={(value: SingleValue<Dropdown>) => {
								if (user?.pifMeta?.underGraduate?.degree || user?.pifMeta?.postGraduate?.degree) {
									return;
								}
								updateFormData('degree', value?.value ?? '');
								updateFormData('specialisation', '');
							}}
							options={degreeOptions}
						/>
					</div>
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="specialisation" value="UG/PG Specialisation" />
							<span className="text-[#555770] text-xs"> (Whichever is latest)</span>
							<span className="text-[red]"> *</span>
						</div>

						<Select
							required
							isClearable
							id="specialisation"
							isDisabled={
								!!user?.pifMeta?.underGraduate?.specialisation || !!user?.pifMeta?.postGraduate?.specialisation
							}
							options={specialisationOptions}
							value={
								formData.specialisation
									? specialisationOptions?.find(item => item.value === formData.specialisation)
									: null
							}
							onChange={(value: SingleValue<Dropdown>) => {
								if (user?.pifMeta?.underGraduate?.specialisation || user?.pifMeta?.postGraduate?.specialisation) {
									return;
								}
								updateFormData('specialisation', value?.value ?? '');
							}}
							classNamePrefix="flowbite-select"
						/>
					</div>
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label
								htmlFor="currentScore"
								value={
									user?.userPreference?.experienceLevel === UserExperienceLevelType.FINAL_YEAR_STUDENT
										? 'Current GPA'
										: 'UG/PG Graduation Score'
								}
							/>
							<span className="text-[red]"> *</span>
						</div>
						<TextInput
							color="purple"
							id="currentScore"
							type="number"
							className="RemoveInputArrow [&_input]:pr-[193px]"
							sizing="lg"
							min={0}
							step="0.01"
							disabled={!!user?.pifMeta?.underGraduate?.percentage || !!user?.pifMeta?.postGraduate?.percentage}
							max={currentScoreOptions?.outOf}
							value={formData.currentScore}
							onChange={e => {
								if (user?.pifMeta?.underGraduate?.percentage || user?.pifMeta?.postGraduate?.percentage) {
									return;
								}
								if (
									(currentScoreOptions && Number(e.target.value) > currentScoreOptions?.outOf) ||
									Number(e.target.value) < 0
								) {
									return;
								}
								updateFormData('currentScore', e.target.value);
							}}
							required
							placeholder={`Out of ${currentScoreOptions?.outOf}`}
							rightIcon={() => {
								return (
									<div className="pointer-events-auto border-l-[2px] -mr-3 border-[#D1D5DB] h-full flex items-center px-4 relative min-w-[194px] justify-between">
										<CustomDropDown
											buttonClassName="justify-between"
											label={currentScoreOptions?.label || ''}
											disabled={!!user?.pifMeta?.underGraduate?.scoreType || !!user?.pifMeta?.postGraduate?.scoreType}
											buttonStyle={{
												color: '#6B7280',
											}}
											options={options}
											onChange={value => {
												if (user?.pifMeta?.underGraduate?.scoreType || user?.pifMeta?.postGraduate?.scoreType) {
													return;
												}
												updateFormData('currentScoreType', value as ScoreType);
												updateFormData('currentScore', '');
											}}
										/>
									</div>
								);
							}}
						/>
					</div>
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="gradYear" value="Graduation Year" />
							<span className="text-[red]"> *</span>
						</div>

						<Select
							id="gradYear"
							required
							isClearable
							menuPlacement="auto"
							options={futureGradYearOptions}
							classNamePrefix="flowbite-select"
							isDisabled={!!user?.profile?.gradYear}
							value={futureGradYearOptions?.find(item => Number(item.value) === Number(formData.gradYear))}
							onChange={value => {
								if (user?.profile?.gradYear) {
									return;
								}
								updateFormData('gradYear', value?.value ?? '');
							}}
						/>
					</div>
				</div>

				<div className="flex items-end gap-3 pt-[38px]">
					<h2 className="text-xl font-semibold w-fit">Basic details</h2>
					<div className="h-full border-t-[1px] border-[#C7C9D9] flex-1" />
				</div>

				<div className="flex items-center flex-wrap gap-2 md:gap-[48px] w-full max-w-[904px] mt-5">
					<div className="md:flex-1 w-full">
						<div className="mb-2">
							<Label htmlFor="currentScore" value="Gender" />
							<span className="text-[red]"> *</span>
						</div>

						<div className="flex flex-wrap gap-2">
							<button
								type="button"
								disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
								onClick={() => {
									if (user?.pifMeta?.gender || user?.profile?.gender) {
										return;
									}
									updateFormData('gender', 'Male');
								}}
								className={classNames(
									'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
									formData.gender === 'Male'
										? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
										: 'bg-white	text-black',
								)}
							>
								Male
							</button>
							<button
								type="button"
								disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
								onClick={() => {
									if (user?.pifMeta?.gender || user?.profile?.gender) {
										return;
									}
									updateFormData('gender', 'Female');
								}}
								className={classNames(
									'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
									formData.gender === 'Female'
										? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
										: 'bg-white	text-black',
								)}
							>
								Female
							</button>
							<button
								type="button"
								disabled={!!user?.pifMeta?.gender || !!user?.profile?.gender}
								onClick={() => {
									if (user?.pifMeta?.gender || user?.profile?.gender) {
										return;
									}
									updateFormData('gender', 'Other');
								}}
								className={classNames(
									'flex flex-1 !text-center md:flex-[0] justify-center px-[12px] py-[8px] cursor-pointer rounded-lg border w-max disabled:opacity-50',
									formData.gender === 'Other'
										? 'bg-[#18222D] border border-[#7777E7]	text-gray-50'
										: 'bg-white	text-black',
								)}
							>
								Other
							</button>
						</div>
					</div>

					{!user?.flags?.isMasaiStudent ? (
						<div className="md:flex-1 w-full">
							<div className="md:flex-1 w-full">
								<div className="mb-2">
									<Label htmlFor="resume" value="Resume" />
									{formData?.resume ? (
										<a href={formData?.resume} target="_blank" className="text-[#7777E7] underline text-xs ml-1">
											(Preview){' '}
										</a>
									) : (
										<span className="text-[red]"> *</span>
									)}
								</div>
								<FileInput
									id="resume"
									sizing="lg"
									disabled={!!(user?.flags?.isMasaiStudent && formData?.resume)}
									accept="application/pdf"
									required={formData?.resume ? false : true}
									onChange={e => {
										if (user?.subRole && user?.flags?.isMasaiStudent && formData?.resume) {
											return;
										}
										if (e.target.files) setResume(e.target.files[0]);
									}}
								/>
								<p className="text-xs text-gray-500 italic mt-3">Only pdf files up to 5mb</p>
							</div>
						</div>
					) : null}
				</div>

				<div className="flex items-center gap-2 mt-[43px]">
					<Button
						size="lg"
						type="submit"
						color="purplePlaced"
						className="font-inter w-full max-w-[167px]"
						disabled={updateUserProfileDataFn.isLoading}
						isProcessing={updateUserProfileDataFn.isLoading}
					>
						Next <HiOutlineArrowRight className="ml-2" />
					</Button>
				</div>
			</form>
		</div>
	);
};

export default MandatoryForm;
